import React, { useState } from "react";
import { Button, Card, Col, Row, Nav, Modal, ProgressBar } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useBill } from '../hooks/useBill';
import { BillForm } from '../components/Bill/BillForm';
import { BillStatsCards } from '../components/Bill/BillStatsCards';
import { BillTable } from '../components/Bill/BillTable';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactApexChart from "react-apexcharts";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ChartWrapper } from '../components/charts/ChartWrapper';
import { NoDataMessage } from '../components/common/NoDataMessage';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Bill() {
    const [showModal, setShowModal] = useState(false);
    const [selectedBill, setSelectedBill] = useState(null);
    const [filter, setFilter] = useState('all');
    const {
        bills,
        categories,
        loading,
        stats,
        addBill,
        updateBill,
        deleteBill,
        updateBillStatus
    } = useBill();

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBill(null);
    };

    const handleSubmit = async (values, formikHelpers) => {
        const submitFn = selectedBill ? updateBill : addBill;
        const result = await submitFn(values, formikHelpers);
        
        if (result.success) {
            toast.success(result.message);
            handleCloseModal();
        } else {
            toast.error(result.message);
        }
    };

    const handleStatusChange = async (billId, newStatus) => {
        const result = await updateBillStatus(billId, newStatus);
        if (result.success) {
            toast.success(result.message);
        } else {
            toast.error(result.message);
        }
    };

    const filteredBills = bills.filter(bill => {
        if (filter === 'all') return true;
        return bill.status === filter;
    });

    const getCategoryData = () => {
        const categoryTotals = {};
        bills.forEach(bill => {
            if (!categoryTotals[bill.category]) {
                categoryTotals[bill.category] = 0;
            }
            categoryTotals[bill.category] += parseFloat(bill.amount);
        });

        return {
            series: Object.values(categoryTotals),
            options: {
                chart: {
                    type: 'donut',
                },
                labels: Object.keys(categoryTotals),
                colors: ['#6259ca', '#f99433', '#01b8ff', '#f7b731', '#4ec2f0'],
                legend: {
                    position: 'bottom'
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    formatter: function (w) {
                                        return '$' + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    const getStatusData = () => {
        const statusTotals = {
            paid: bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + parseFloat(b.amount), 0),
            pending: bills.filter(b => b.status === 'pending').reduce((sum, b) => sum + parseFloat(b.amount), 0),
            overdue: bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + parseFloat(b.amount), 0)
        };

        return {
            series: Object.values(statusTotals),
            options: {
                chart: {
                    type: 'donut',
                },
                labels: ['Paid', 'Pending', 'Overdue'],
                colors: ['#33d685', '#dc3545', '#f7b731'],
                legend: {
                    position: 'bottom'
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    formatter: function (w) {
                                        return '$' + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    const getTrendData = () => {
        const monthlyData = bills.reduce((acc, bill) => {
            const month = new Date(bill.dueDate).toLocaleString('default', { month: 'short' });
            acc[month] = (acc[month] || 0) + Number(bill.amount);
            return acc;
        }, {});

        const hasData = Object.keys(monthlyData).length > 0;

        return {
            series: [{
                name: 'Bills',
                data: hasData ? Object.values(monthlyData) : [0]
            }],
            options: {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: false
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                xaxis: {
                    categories: hasData ? Object.keys(monthlyData) : ['No Data'],
                    labels: {
                        style: {
                            colors: '#6e7985',
                            fontSize: '11px'
                        }
                    },
                    axisBorder: {
                        show: false
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: '#6e7985',
                            fontSize: '11px'
                        },
                        formatter: function(value) {
                            return `$${value.toFixed(0)}`;
                        }
                    }
                },
                grid: {
                    borderColor: 'rgba(72,94,144, 0.07)',
                    padding: {
                        top: -20
                    },
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                colors: ['#506fd9'],
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.3,
                        stops: [0, 90, 100]
                    }
                },
                tooltip: {
                    y: {
                        formatter: (value) => `$${value.toFixed(2)}`
                    }
                },
                legend: {
                    show: false
                }
            }
        };
    };

    const renderChart = (title, chartData, icon = "ri-pie-chart-line") => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h3">{title}</Card.Title>
            </Card.Header>
            <Card.Body>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : bills.length === 0 ? (
                    <NoDataMessage 
                        icon={icon}
                        message="No bill data available"
                    />
                ) : (
                    <ReactApexChart
                        options={chartData.options}
                        series={chartData.series}
                        type="donut"
                        height={300}
                    />
                )}
            </Card.Body>
        </Card>
    );

    const renderTrendChart = () => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h6">Bill Trends</Card.Title>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : bills.length === 0 ? (
                    <NoDataMessage 
                        icon="ri-line-chart-line"
                        message="No bill data available"
                    />
                ) : (
                    <ReactApexChart
                        options={getTrendData().options}
                        series={getTrendData().series}
                        type="area"
                        height="100%"
                    />
                )}
            </Card.Body>
        </Card>
    );

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Bills</h4>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                    >
                        Add New Bill
                    </Button>
                </div>

                <BillStatsCards stats={stats} />

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <Nav className="nav-pills">
                                <Nav.Link
                                    active={filter === 'all'}
                                    onClick={() => setFilter('all')}
                                >All</Nav.Link>
                                <Nav.Link
                                    active={filter === 'pending'}
                                    onClick={() => setFilter('pending')}
                                >Pending</Nav.Link>
                                <Nav.Link
                                    active={filter === 'paid'}
                                    onClick={() => setFilter('paid')}
                                >Paid</Nav.Link>
                                <Nav.Link
                                    active={filter === 'overdue'}
                                    onClick={() => setFilter('overdue')}
                                >Overdue</Nav.Link>
                            </Nav>
                        </div>

                        <BillTable 
                            bills={filteredBills}
                            onDelete={deleteBill}
                            onEdit={(bill) => {
                                setSelectedBill(bill);
                                setShowModal(true);
                            }}
                            onStatusChange={handleStatusChange}
                        />
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    <Col lg={4} md={12}>
                        {renderChart('Bills by Category', getCategoryData())}
                    </Col>
                    <Col lg={4} md={12}>
                        {renderChart('Bills by Status', getStatusData())}
                    </Col>
                    <Col lg={4} md={12}>
                        {renderTrendChart()}
                    </Col>
                </Row>
            
                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedBill ? 'Edit Bill' : 'Add New Bill'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <BillForm
                            categories={categories}
                            initialValues={selectedBill}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
} 